import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, MapPin, Clock, Sparkle } from "@phosphor-icons/react";
import { travelDateApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import { TierBadge } from "@/pages/TravelDate";
import { useTranslation } from "react-i18next";

const CORAL = "#F4A28F";

export default function TravelDatePublic() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const [d, setD] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        travelDateApi.publicShare(slug).then(setD).catch(() => setError(t("travelDatePublic.notFound.defaultErr")));
    }, [slug, t]);
    if (error) return (
        <div className="min-h-screen grid place-items-center text-center px-6">
            <div>
                <div className="text-5xl mb-3">🔒</div>
                <div className="font-serif text-3xl text-white mb-2">{t("travelDatePublic.notFound.title")}</div>
                <div className="text-white/60 mb-6">{error}</div>
                <Link to="/traveldate" className="text-xs !py-2 !px-4 rounded-full font-semibold inline-flex items-center gap-1.5" style={{ background: CORAL, color: "var(--navy)" }}>{t("travelDatePublic.notFound.btn")}</Link>
            </div>
        </div>
    );
    if (!d) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;
    return (
        <div className="min-h-screen grain" data-testid="td-public">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(244,162,143,0.15)" }}>
                <div className="max-w-[1000px] mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <PassportLogo size={26} />
                        <div className="hidden sm:block leading-none">
                            <div className="font-serif text-base text-white">TravelDate™</div>
                            <div className="text-[9px] tracking-[0.3em] uppercase" style={{ color: CORAL }}>{t("travelDatePublic.sharedDate")}</div>
                        </div>
                    </Link>
                    <Link to="/traveldate" className="text-xs !py-2 !px-4 rounded-full font-semibold inline-flex items-center gap-1.5" style={{ background: CORAL, color: "var(--navy)" }} data-testid="td-public-cta">
                        <Heart size={11} weight="fill" /> {t("travelDatePublic.planYourOwn")}
                    </Link>
                </div>
            </header>
            <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-10">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[16/7] mb-8" style={{ border: "1px solid rgba(244,162,143,0.3)" }}>
                    <img src={d.hero_image} alt={d.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,19,32,0.2) 0%, rgba(5,10,17,0.92) 100%)" }} />
                    <div className="relative h-full p-6 md:p-10 flex flex-col justify-end">
                        <div className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: CORAL }}>{d.shared_by || "Passport"} · {t("travelDatePublic.sharedTravelDate")}</div>
                        <div className="flex flex-wrap items-center gap-3 mb-3">
                            <TierBadge tier={d.tier} />
                            <span className="rounded-full px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-medium" style={{ background: "rgba(11,19,32,0.7)", color: CORAL, border: `1px solid ${CORAL}66`, backdropFilter: "blur(6px)" }}>{t("travelDateDetail.score", { score: d.score })}</span>
                        </div>
                        <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl text-white leading-[1] tracking-tight">❤️ {d.title}</h1>
                        <div className="mt-3 flex flex-wrap gap-3 text-white/85 text-sm">
                            <span className="inline-flex items-center gap-1"><MapPin size={12} weight="fill" color={CORAL} /> {d.city}{d.country ? `, ${d.country}` : ""}</span>
                            <span className="inline-flex items-center gap-1"><Clock size={12} weight="fill" color={CORAL} /> {d.duration_hours}h</span>
                            {d.estimated_cost_usd ? <span>· ~${d.estimated_cost_usd}</span> : null}
                        </div>
                    </div>
                </motion.div>

                {d.vibe && <div className="text-lg text-white/85 italic mb-3">"{d.vibe}"</div>}
                {d.why_this_works && <p className="text-white/75 mb-8 leading-relaxed">{d.why_this_works}</p>}

                <div className="overline mb-4">{t("travelDatePublic.itineraryOverline")}</div>
                <div className="space-y-3">
                    {(d.itinerary || []).map((s, i) => (
                        <div key={i} className="rounded-2xl p-4 flex items-start gap-4" style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}>
                            <div className="text-[11px] uppercase tracking-[0.15em]" style={{ color: CORAL, minWidth: 62 }}>{s.time}</div>
                            <div className="w-10 h-10 rounded-2xl grid place-items-center shrink-0 text-lg" style={{ background: "rgba(244,162,143,0.12)", border: `1px solid ${CORAL}44` }}>{s.icon || "❤"}</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-medium leading-tight">{s.title}</div>
                                {s.subtitle && <div className="text-white/60 text-[13px] mt-1">{s.subtitle}</div>}
                                <div className="text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1.5">{s.duration_minutes} min{s.est_cost_usd ? ` · ~$${s.est_cost_usd}` : ""}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 text-center rounded-3xl p-8" style={{ background: "var(--navy-deep)", border: `1px solid ${CORAL}44` }}>
                    <div className="text-3xl mb-3">❤️</div>
                    <div className="font-serif text-3xl text-white mb-2">{t("travelDatePublic.planYourOwnBox.title")}</div>
                    <div className="text-white/60 mb-5 max-w-md mx-auto">{t("travelDatePublic.planYourOwnBox.sub")}</div>
                    <Link to="/traveldate" className="text-xs !py-2 !px-4 rounded-full font-semibold inline-flex items-center gap-1.5" style={{ background: CORAL, color: "var(--navy)" }}>
                        <Heart size={11} weight="fill" /> {t("travelDatePublic.planYourOwnBox.btn")}
                    </Link>
                </div>
                <div className="text-center text-[10px] uppercase tracking-[0.3em] text-white/40 mt-8">
                    {t("travelDatePublic.footer")}
                </div>
            </div>
        </div>
    );
}
